import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import ProductShowcase from '@/components/base/ProductShowcase';
import { useSchemaLD } from '@/hooks/useSEO';
import { waLink, SITE_URL } from '@/config/site';
import {
  bancosUrbanos,
  bancosUrbanosComplementares,
  avisoImagens,
  avisoProjetos,
} from '@/mocks/curadoria';

const categorias = [
  {
    titulo: 'Bancos para praças',
    descricao: 'Fixação no piso, uso coletivo e composições lineares.',
  },
  {
    titulo: 'Bancos para jardins',
    descricao: 'Peças ripadas e curvas integradas ao paisagismo.',
  },
  {
    titulo: 'Assentos de convivência',
    descricao: 'Terraços, átrios e áreas de permanência prolongada.',
  },
  {
    titulo: 'Bicicletários',
    descricao: 'Apoio à mobilidade em áreas públicas e institucionais.',
  },
  {
    titulo: 'Lixeiras urbanas',
    descricao: 'Estações de descarte e complementos de praça.',
  },
  {
    titulo: 'Mesas e complementos',
    descricao: 'Apoio, jogos e permanência em áreas abertas.',
  },
];

const fichaUrbano = [
  { label: 'Aplicação', valor: 'Praças, parques, jardins e áreas comuns' },
  { label: 'Fixação', valor: 'Definida por modelo e tipo de piso' },
  { label: 'Acabamentos', valor: 'Conforme ficha técnica do fabricante' },
  { label: 'Cotação', valor: 'Quantitativo definido por área' },
];

const aplicacoes = [
  'Praças públicas',
  'Parques municipais',
  'Condomínios',
  'Universidades',
  'Empresas',
  'Áreas comuns',
  'Instituições públicas',
];

const materiais = [
  'Estruturas metálicas e madeira plástica conforme composição informada pelo fabricante.',
  'Acabamentos, cores e fixação definidos por modelo — confirmados na ficha técnica.',
  'Indicação de uso externo validada item a item antes da especificação.',
  'Quantitativos e prazos definidos na cotação, conforme escopo do órgão ou empresa.',
];

export default function MobiliarioUrbanoPage() {
  useSchemaLD({
    id: 'schema-mobiliario-urbano',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Mobiliário urbano para praças e parques',
      description:
        'Bancos, bicicletários e soluções de mobiliário urbano para praças, parques, jardins e espaços de convivência. Consulte modelos e solicite cotação.',
      url: `${SITE_URL}/mobiliario-urbano`,
      isPartOf: { '@type': 'WebSite', name: 'Home Office Premium', url: SITE_URL },
      about: 'Mobiliário urbano e mobiliário para espaços coletivos',
    },
  });

  useSchemaLD({
    id: 'schema-mobiliario-urbano-breadcrumb',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Soluções', item: `${SITE_URL}/produtos` },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Mobiliário urbano',
          item: `${SITE_URL}/mobiliario-urbano`,
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-14 md:pt-16">
        {/* 1 — HERO */}
        <section className="bg-background-50">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 pt-10 md:pt-16 pb-8 md:pb-14">
            <nav aria-label="Você está aqui" className="flex flex-wrap items-center gap-2 text-[11.5px] text-foreground-400">
              <Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer">
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-[12px]"></i>
              <Link to="/produtos" className="hover:text-foreground-800 transition-colors cursor-pointer">
                Soluções
              </Link>
              <i className="ri-arrow-right-s-line text-[12px]"></i>
              <span className="text-foreground-700">Mobiliário urbano</span>
            </nav>

            <div className="mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
              <div className="lg:col-span-6">
                <p className="hop-eyebrow text-foreground-400">Espaços de convivência</p>
                <h1 className="mt-4 text-[32px] sm:text-[42px] md:text-[52px] leading-[1.04] tracking-[-0.038em] font-semibold text-foreground-950">
                  Mobiliário para espaços que recebem pessoas.
                </h1>
                <p className="mt-5 text-[15px] md:text-[17px] leading-[1.6] text-foreground-500 max-w-[500px]">
                  Bancos, bicicletários e soluções para praças, parques, jardins e áreas de
                  convivência.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                  <a
                    href={waLink('urbano')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-foreground-950 text-white text-[14px] md:text-[15px] font-medium px-6 py-3.5 rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-[15px]"></i>
                    Solicitar cotação de mobiliário urbano
                  </a>
                  <Link
                    to="/fornecimento-orgaos-publicos"
                    className="group inline-flex items-center justify-center gap-1.5 text-foreground-800 text-[14px] md:text-[15px] font-medium py-3 whitespace-nowrap cursor-pointer"
                  >
                    Setor público
                    <i className="ri-arrow-right-line text-[13px] text-primary-600 transition-transform duration-300 group-hover:translate-x-1"></i>
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="border-t border-background-300">
                  {fichaUrbano.map((item) => (
                    <div
                      key={item.label}
                      className="flex items-start justify-between gap-6 py-4 border-b border-background-200"
                    >
                      <span className="text-[10.5px] uppercase tracking-[0.16em] text-foreground-400 whitespace-nowrap pt-[3px]">
                        {item.label}
                      </span>
                      <span className="text-[13px] md:text-[13.5px] leading-snug text-foreground-800 text-right max-w-[280px]">
                        {item.valor}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-[11.5px] leading-relaxed text-foreground-400">
                  Modelos e acabamentos confirmados por item antes da cotação.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 2 — APRESENTAÇÃO */}
        <section className="border-y border-background-200 bg-white">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 py-12 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
              <div className="lg:col-span-5">
                <p className="hop-eyebrow text-foreground-400">O que é mobiliário urbano</p>
                <h2 className="mt-4 text-[24px] sm:text-[30px] md:text-[36px] leading-[1.1] tracking-[-0.034em] font-semibold text-foreground-950">
                  O mobiliário define como a cidade é usada.
                </h2>
              </div>
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                <p className="text-[14px] leading-[1.7] text-foreground-600">
                  Bancos, assentos, bicicletários, lixeiras e mesas organizam a permanência das
                  pessoas no espaço público. Um conjunto bem especificado aumenta o tempo de
                  convivência, reduz a depredação e facilita a manutenção.
                </p>
                <p className="text-[14px] leading-[1.7] text-foreground-600">
                  A Home Office Premium fornece para prefeituras, condomínios, universidades e
                  empresas, com apoio na escolha de modelos, quantitativos e composição do
                  conjunto por área.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 3 — GRID EDITORIAL DE CATEGORIAS */}
        <section className="bg-background-50">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-14 items-end mb-10 md:mb-14">
              <div className="lg:col-span-7">
                <p className="hop-eyebrow text-foreground-400">Categorias</p>
                <h2 className="mt-4 text-[26px] sm:text-[32px] md:text-[40px] leading-[1.08] tracking-[-0.036em] font-semibold text-foreground-950">
                  Seis famílias para compor o espaço.
                </h2>
              </div>
              <div className="lg:col-span-5">
                <p className="text-[14px] md:text-[15px] leading-[1.65] text-foreground-500 max-w-[420px]">
                  Cada categoria reúne modelos de fabricante identificado. Nenhum item entra no
                  site sem referência de catálogo.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-background-200 border border-background-200 rounded-[14px] overflow-hidden">
              {categorias.map((categoria, index) => (
                <article
                  key={categoria.titulo}
                  className="bg-background-50 flex flex-col justify-between gap-8 p-5 md:p-6 min-h-[170px] md:min-h-[190px]"
                >
                  <span className="text-[10.5px] tracking-[0.14em] text-foreground-300">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3 className="text-[16px] md:text-[17px] font-medium tracking-[-0.022em] text-foreground-950">
                      {categoria.titulo}
                    </h3>
                    <p className="mt-1.5 text-[13px] leading-[1.6] text-foreground-500">
                      {categoria.descricao}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* 3b — MODELOS SELECIONADOS */}
        <section className="bg-white border-y border-background-200">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-14 items-end mb-10 md:mb-14">
              <div className="lg:col-span-7">
                <p className="hop-eyebrow text-foreground-400">Linha Artesian · Bancos</p>
                <h2 className="mt-4 text-[24px] sm:text-[30px] md:text-[36px] leading-[1.08] tracking-[-0.034em] font-semibold text-foreground-950">
                  Seis modelos selecionados do catálogo.
                </h2>
              </div>
              <div className="lg:col-span-5">
                <p className="text-[13.5px] md:text-[14.5px] leading-[1.65] text-foreground-500 max-w-[420px]">
                  Modelos retirados da página 22 do catálogo Artesian 2026. Os códigos são os
                  originais do fabricante.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12 md:gap-x-8 md:gap-y-16">
              {bancosUrbanos.map((banco) => (
                <ProductShowcase
                  key={banco.referencia}
                  produto={banco}
                  contexto="mobiliario-urbano"
                  mostrarValidacao
                />
              ))}
            </div>

            <div className="mt-14 rounded-[16px] border border-background-200 bg-background-50 px-5 md:px-8 py-6 md:py-7">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div className="max-w-[420px]">
                  <h3 className="text-[15px] font-medium tracking-[-0.02em] text-foreground-950">
                    Também na mesma página do catálogo
                  </h3>
                  <p className="mt-1.5 text-[12.5px] leading-[1.6] text-foreground-500">
                    Disponibilidade e acabamento sujeitos a confirmação com o fabricante antes de
                    qualquer proposta.
                  </p>
                </div>
                <ul className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4">
                  {bancosUrbanosComplementares.map((item) => (
                    <li key={item.referencia} className="border-t border-background-200 pt-3">
                      <p className="text-[13.5px] font-medium text-foreground-900">{item.nome}</p>
                      <p className="mt-0.5 text-[11.5px] tracking-[0.06em] uppercase text-foreground-400">
                        {item.fabricante} · {item.referencia}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 4 — APLICAÇÕES */}
        <section className="bg-background-50">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
              <div className="lg:col-span-5">
                <p className="hop-eyebrow text-foreground-400">Aplicações</p>
                <h2 className="mt-4 text-[26px] sm:text-[32px] md:text-[38px] leading-[1.08] tracking-[-0.036em] font-semibold text-foreground-950">
                  Onde esse mobiliário trabalha.
                </h2>
                <p className="mt-4 text-[14px] md:text-[15px] leading-[1.65] text-foreground-500 max-w-[400px]">
                  Primeiro contato sem cadastro e sem CNPJ. O detalhamento técnico entra na etapa
                  de cotação.
                </p>
                <a
                  href={waLink('orgaoPublico')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-7 inline-flex items-center justify-center gap-2 bg-foreground-950 text-white text-[14px] font-medium px-6 py-3 rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-[15px]"></i>
                  Solicitar catálogo e cotação
                </a>
              </div>

              <div className="lg:col-span-7">
                <ul className="grid grid-cols-1 sm:grid-cols-2 border-t border-background-200">
                  {aplicacoes.map((aplicacao) => (
                    <li
                      key={aplicacao}
                      className="flex items-center gap-3 py-4 border-b border-background-200 sm:odd:pr-6"
                    >
                      <i className="ri-checkbox-circle-line text-[15px] text-secondary-500"></i>
                      <span className="text-[14px] text-foreground-700">{aplicacao}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 5 — MATERIAIS E CARACTERÍSTICAS */}
        <section className="bg-white border-y border-background-200">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 py-14 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
              <div className="lg:col-span-4">
                <p className="hop-eyebrow text-foreground-400">Materiais e características</p>
                <h2 className="mt-4 text-[24px] sm:text-[28px] md:text-[32px] leading-[1.1] tracking-[-0.034em] font-semibold text-foreground-950">
                  O que confirmamos antes de especificar.
                </h2>
              </div>
              <div className="lg:col-span-8">
                <ul className="border-t border-background-200">
                  {materiais.map((item, index) => (
                    <li
                      key={item}
                      className="flex items-start gap-4 py-4 border-b border-background-200"
                    >
                      <span className="mt-[3px] text-[11px] font-medium tracking-[0.14em] text-foreground-300">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="text-[13.5px] leading-[1.6] text-foreground-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-6 flex items-start gap-2 text-[11.5px] leading-[1.6] text-foreground-400">
                  <i className="ri-error-warning-line mt-[1px] text-[13px] text-accent-500"></i>
                  <span>
                    Não declaramos resistência climática, carga suportada ou indicação de uso
                    externo sem ficha técnica do fabricante. Esses dados são enviados sob consulta
                    antes da especificação.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* AVISOS */}
        <section className="bg-background-50">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 py-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
              <p className="flex items-start gap-2.5 text-[11.5px] leading-[1.65] text-foreground-400">
                <i className="ri-image-line mt-[1px] text-[14px] text-foreground-300"></i>
                <span>{avisoImagens}</span>
              </p>
              <p className="flex items-start gap-2.5 text-[11.5px] leading-[1.65] text-foreground-400">
                <i className="ri-shield-check-line mt-[1px] text-[14px] text-foreground-300"></i>
                <span>{avisoProjetos}</span>
              </p>
            </div>
          </div>
        </section>

        {/* 6 — CTA */}
        <section className="pb-16 md:pb-24 bg-background-50">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10">
            <div className="rounded-[20px] md:rounded-[26px] bg-foreground-950 px-6 sm:px-10 md:px-14 py-12 md:py-16 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div>
                <h2 className="text-[24px] sm:text-[28px] md:text-[34px] leading-[1.1] tracking-[-0.035em] font-semibold text-background-50 max-w-[540px]">
                  Solicitar cotação de mobiliário urbano
                </h2>
                <p className="mt-3 text-[14px] leading-[1.6] text-background-50/55 max-w-[460px]">
                  Informe o tipo de espaço e a quantidade prevista. Retornamos com modelos
                  compatíveis e as condições de fornecimento.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                <a
                  href={waLink('urbano')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-background-50 text-foreground-950 text-[14px] font-medium px-6 py-3.5 rounded-full hover:bg-white transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-[15px]"></i>
                  WhatsApp comercial
                </a>
                <Link
                  to="/mobiliario-pracas-alimentacao"
                  className="inline-flex items-center justify-center gap-1.5 border border-white/20 text-background-50 text-[14px] font-medium px-6 py-3.5 rounded-full hover:border-white/50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Praças de alimentação
                  <i className="ri-arrow-right-line text-[12px]"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}